// Mock data for the "Store Cube Carton Update" IFP report.
// Baseline values (chart bars + "Totals by Week" table) are transcribed
// directly from the reference design screenshot so the default-filter
// ("All" everywhere, Metric = Cartons) render matches it exactly.
//
// The chart ("Average Metric Per Store") and the table ("Totals by Week")
// are modeled as two independent baseline datasets, as they would be in the
// real Power BI semantic model (different aggregation contexts — a per-store
// average vs. a grand total) — they are not arithmetically reconciled to
// each other, and their period ranges genuinely differ (the chart covers
// 202622-202629; the table covers 202622-202629 + 202631, skipping 202630).

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function normalizedFromHash(seed: number, salt: string): number {
  return (hashString(`${seed}:${salt}`) % 10000) / 10000
}

function shrinkForFilter(name: string, value: string): number {
  const norm = normalizedFromHash(hashString(`${name}:${value}`), 'shrink')
  return 0.4 + norm * 0.35 // [0.4, 0.75]
}

export const DC_OPTIONS = [
  '1_Dallas',
  '2_Atlanta',
  '3_Memphis',
  '4_Indianapolis',
  '5_Los Angeles',
]

export const EVENT_OPTIONS: string[] = Array.from(
  { length: 10 },
  (_, i) => `SEA${String(i + 1).padStart(2, '0')}`
)

export const SOURCE_OPTIONS = ['PO', 'JDA', 'Planned']

export const METRIC_OPTIONS = [
  'Cartons',
  'Cube (cbm)',
  'Cube (cft)',
  'Sellable Units',
]

// Fixed anchor so the option list is stable across renders/sessions rather
// than depending on the actual current date.
const SNAPSHOT_ANCHOR = new Date('2026-07-14')

function lastFridayOnOrBefore(date: Date): Date {
  const next = new Date(date)
  const day = next.getDay() // 0=Sun...6=Sat, Fri=5
  const diff = (day - 5 + 7) % 7
  next.setDate(next.getDate() - diff)
  return next
}

function formatSnapshotDate(date: Date): string {
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
}

const RECENT_FRIDAYS: string[] = (() => {
  const fridays: string[] = []
  let cursor = lastFridayOnOrBefore(SNAPSHOT_ANCHOR)
  for (let i = 0; i < 4; i++) {
    fridays.push(formatSnapshotDate(cursor))
    cursor = new Date(cursor)
    cursor.setDate(cursor.getDate() - 7)
  }
  return fridays
})()

export const SNAPSHOT_BASE_OPTIONS = ['Latest', ...RECENT_FRIDAYS]

export interface StoreCubeCartonFilterState {
  snapshotBase: string
  source: string | null
  season: string | null
  event: string | null
  dc: string | null
  metric: string
  divisionId: string | null
  departmentId: string | null
  classId: string | null
  subclassId: string | null
}

function cosmeticFilterPairs(
  filters: StoreCubeCartonFilterState
): Array<[string, string]> {
  const pairs: Array<[string, string]> = []
  if (filters.season) pairs.push(['season', filters.season])
  if (filters.event) pairs.push(['event', filters.event])
  if (filters.divisionId) pairs.push(['division', filters.divisionId])
  if (filters.departmentId) pairs.push(['department', filters.departmentId])
  if (filters.classId) pairs.push(['class', filters.classId])
  if (filters.subclassId) pairs.push(['subclass', filters.subclassId])
  if (filters.dc) pairs.push(['dc', filters.dc])
  if (filters.source) pairs.push(['source', filters.source])
  if (filters.snapshotBase !== 'Latest')
    pairs.push(['snapshotBase', filters.snapshotBase])
  return pairs
}

function totalShrinkFor(filters: StoreCubeCartonFilterState): number {
  return cosmeticFilterPairs(filters).reduce(
    (acc, [name, value]) => acc * shrinkForFilter(name, value),
    1
  )
}

function wiggleSeedFor(filters: StoreCubeCartonFilterState): number {
  const pairs = cosmeticFilterPairs(filters)
  return pairs.length
    ? hashString(pairs.map(([n, v]) => `${n}=${v}`).join('|'))
    : 0
}

// Deterministic per-metric multiplier, stable across renders — switching the
// Metric radio scales both datasets by a plausible, internally consistent
// ratio without needing 4x the baseline data. Cartons is the anchor (x1),
// matching the screenshot's default selection.
const METRIC_SEED = hashString('store-cube-carton:metric-conversion')
const CBM_PER_CARTON = 0.03 + normalizedFromHash(METRIC_SEED, 'cbm') * 0.02 // ~0.03-0.05 cbm/carton
const CFT_PER_CARTON = CBM_PER_CARTON * 35.3147 // keep cbm/cft internally consistent
const UNITS_PER_CARTON = 6 + normalizedFromHash(METRIC_SEED, 'units') * 6 // ~6-12 units/carton

export const METRIC_MULTIPLIERS: Record<string, number> = {
  Cartons: 1,
  'Cube (cbm)': CBM_PER_CARTON,
  'Cube (cft)': CFT_PER_CARTON,
  'Sellable Units': UNITS_PER_CARTON,
}

function metricMultiplier(metric: string): number {
  return METRIC_MULTIPLIERS[metric] ?? 1
}

// ---------------------------------------------------------------------------
// Chart: "Average Metric Per Store"
// ---------------------------------------------------------------------------

export const CHART_PERIODS = [
  '202622',
  '202623',
  '202624',
  '202625',
  '202626',
  '202627',
  '202628',
  '202629',
]

interface ChartBaseline {
  core: number
  seaRollover: number
  seaOnTime: number
}

// Transcribed from the screenshot's bar labels. A couple of weeks show no
// visible "SEA Rollover" band (too thin/zero to carry a label) — those are
// given a small non-zero baseline rather than exactly 0, since a real
// per-store average is unlikely to be a hard zero.
const BASELINE_CHART: Record<string, ChartBaseline> = {
  '202622': { core: 346, seaRollover: 81, seaOnTime: 333 },
  '202623': { core: 316, seaRollover: 4, seaOnTime: 396 },
  '202624': { core: 459, seaRollover: 97, seaOnTime: 361 },
  '202625': { core: 359, seaRollover: 4, seaOnTime: 194 },
  '202626': { core: 312, seaRollover: 4, seaOnTime: 114 },
  '202627': { core: 235, seaRollover: 4, seaOnTime: 252 },
  '202628': { core: 199, seaRollover: 8, seaOnTime: 217 },
  '202629': { core: 296, seaRollover: 8, seaOnTime: 228 },
}

export interface ChartBar {
  period: string
  core: number
  seaRollover: number
  seaOnTime: number
}

// "Average Metric Per Store" is a per-store average (a ratio, not a raw
// sum) — narrowing filters shouldn't shrink it the way a grand total
// shrinks. So it only wiggles +-5% per active filter combo, it does not
// multiplicatively shrink.
export function buildChartBars(
  filters: StoreCubeCartonFilterState
): ChartBar[] {
  const wiggleSeed = wiggleSeedFor(filters)
  const multiplier = metricMultiplier(filters.metric)

  return CHART_PERIODS.map(period => {
    const base = BASELINE_CHART[period]
    const noise = (salt: string) =>
      wiggleSeed === 0
        ? 1
        : 1 + (normalizedFromHash(wiggleSeed, `${period}:${salt}`) - 0.5) * 0.1 // +-5%

    return {
      period,
      core: base.core * noise('core') * multiplier,
      seaRollover: base.seaRollover * noise('rollover') * multiplier,
      seaOnTime: base.seaOnTime * noise('onTime') * multiplier,
    }
  })
}

// ---------------------------------------------------------------------------
// Table: "Totals by Week"
// ---------------------------------------------------------------------------

export const PERIOD_COLUMNS = [
  '202622',
  '202623',
  '202624',
  '202625',
  '202626',
  '202627',
  '202628',
  '202629',
  '202631',
]

export type FlowCategory = 'SEA On Time' | 'SEA Rollover' | 'Core'

export const FLOW_CATEGORIES: FlowCategory[] = [
  'SEA On Time',
  'SEA Rollover',
  'Core',
]

// Transcribed from the screenshot's "Totals by Week" table. `null` renders
// as a blank cell (not 0), matching the sparsity actually shown (no
// 202631 activity for the two seasonal categories).
const BASELINE_TABLE: Record<FlowCategory, Record<string, number | null>> = {
  'SEA On Time': {
    '202622': 327719.25,
    '202623': 1028233.6,
    '202624': 347968.55,
    '202625': 717771.0,
    '202626': 122933.31,
    '202627': 752465.78,
    '202628': 537765.05,
    '202629': 710388.89,
    '202631': null,
  },
  'SEA Rollover': {
    '202622': 29447.75,
    '202623': 164116.91,
    '202624': 8463.61,
    '202625': 15630.0,
    '202626': null,
    '202627': 92052.56,
    '202628': 19699.0,
    '202629': 39044.44,
    '202631': null,
  },
  Core: {
    '202622': 518477.61,
    '202623': 820905.49,
    '202624': 486552.38,
    '202625': 770033.84,
    '202626': 765444.62,
    '202627': 279302.29,
    '202628': 324313.26,
    '202629': 712995.75,
    '202631': 398774.32,
  },
}

export interface TableCategoryRow {
  category: FlowCategory
  values: Record<string, number | null>
  total: number
  dcRows: Array<{
    dc: string
    values: Record<string, number | null>
    total: number
  }>
}

export interface TableTotalRow {
  values: Record<string, number | null>
  total: number
}

export interface TotalsByWeekData {
  categoryRows: TableCategoryRow[]
  totalRow: TableTotalRow
}

// Per-DC shares (summing to 1) for splitting a category/period total across
// the 5 DCs — same hash-based-proportion technique used elsewhere in this
// folder to derive row-level shares from a group total.
function dcShares(category: FlowCategory, period: string): number[] {
  const raw = DC_OPTIONS.map(
    (dc, i) =>
      0.5 +
      normalizedFromHash(
        hashString(`${category}:${period}:${dc}:${i}`),
        'share'
      )
  )
  const sum = raw.reduce((a, b) => a + b, 0)
  return raw.map(v => v / sum)
}

export function buildTotalsByWeekRows(
  filters: StoreCubeCartonFilterState
): TotalsByWeekData {
  const shrink = totalShrinkFor(filters)
  const multiplier = metricMultiplier(filters.metric)

  const categoryRows: TableCategoryRow[] = FLOW_CATEGORIES.map(category => {
    const values: Record<string, number | null> = {}
    const dcAccum: Record<string, Record<string, number | null>> = {}
    DC_OPTIONS.forEach(dc => {
      dcAccum[dc] = {}
    })

    PERIOD_COLUMNS.forEach(period => {
      const raw = BASELINE_TABLE[category][period]
      if (raw == null) {
        values[period] = null
        DC_OPTIONS.forEach(dc => {
          dcAccum[dc][period] = null
        })
        return
      }
      // The matrix displays whole units, so aggregate the same integers that
      // users see instead of summing hidden decimal fractions.
      const shrunk = Math.round(raw * shrink * multiplier)
      values[period] = shrunk

      const shares = dcShares(category, period)
      DC_OPTIONS.forEach((dc, i) => {
        dcAccum[dc][period] = shrunk * shares[i]
      })
    })

    const total = PERIOD_COLUMNS.reduce(
      (sum, period) => sum + (values[period] ?? 0),
      0
    )

    const dcRows = DC_OPTIONS.map(dc => ({
      dc,
      values: dcAccum[dc],
      total: PERIOD_COLUMNS.reduce(
        (sum, period) => sum + (dcAccum[dc][period] ?? 0),
        0
      ),
    }))

    return { category, values, total, dcRows }
  })

  // Total row is computed after shrink so it stays internally consistent
  // (self-corrects under wiggle) rather than being transcribed/hardcoded.
  const totalValues: Record<string, number | null> = {}
  PERIOD_COLUMNS.forEach(period => {
    const sum = categoryRows.reduce(
      (acc, row) => acc + (row.values[period] ?? 0),
      0
    )
    const anyPresent = categoryRows.some(row => row.values[period] != null)
    totalValues[period] = anyPresent ? sum : null
  })
  const total = categoryRows.reduce((sum, row) => sum + row.total, 0)

  return {
    categoryRows,
    totalRow: { values: totalValues, total },
  }
}

export function formatTableValue(value: number | null): string {
  if (value == null) return ''
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}
